import type { ReactNode } from 'react';
import { Box, Button, Flex, Grid, Icon, IconButton, Text } from '@/ui';
import { ControlLayer } from './ControlLayer';

type Props = {
  hasImage: boolean;
  isRemovingImage: boolean;
  isUploadingImage: boolean;
  recommended?: ReactNode;
  onRemoveClick: () => void;
  onUploadClick: () => void;
};

export const Controls = ({
  hasImage,
  isRemovingImage,
  isUploadingImage,
  recommended,
  onUploadClick,
  onRemoveClick,
}: Props) => {
  return (
    <Flex
      align="center"
      data-group
      inset={{ base: 1, md: 2 }}
      justify="center"
      pos="absolute"
      sx={{
        containerType: 'inline-size',
      }}
      {...(!hasImage
        ? {
            role: 'button',
            onClick: onUploadClick,
            cursor: 'pointer',
            _hover: { bg: 'purple.100' },
            bg: 'purple.50',
            borderColor: 'purple.400',
            borderRadius: 'var(--experience-card-radius)',
            borderStyle: 'dashed',
            borderWidth: '2px',
            color: 'purple.500',
          }
        : null)}
    >
      <ControlLayer hasImage={hasImage} isCompact={false}>
        <Grid
          alignItems="center"
          h="100%"
          justifyItems="center"
          templateRows="repeat(3, 1fr)"
        >
          <Box />

          <Flex align="center" gap="2" justify="center">
            {hasImage ? (
              <>
                <Button
                  fontSize="md"
                  isDisabled={isRemovingImage}
                  isLoading={isUploadingImage}
                  leftIcon={<Icon icon="Upload" size="22px" />}
                  loadingText="Uploading..."
                  onClick={onUploadClick}
                >
                  Replace
                </Button>

                <Button
                  fontSize="md"
                  isDisabled={isUploadingImage}
                  isLoading={isRemovingImage}
                  leftIcon={<Icon icon="X" size="22px" />}
                  loadingText="Removing..."
                  onClick={onRemoveClick}
                >
                  Remove
                </Button>
              </>
            ) : (
              <Text alignItems="center" display="flex" gap="2">
                <Icon icon="Upload" size="22px" />
                <Text as="span">Upload</Text>
              </Text>
            )}
          </Flex>

          {recommended && (
            <Text
              _groupHover={{ opacity: 1 }}
              bg="blackAlpha.500"
              borderRadius="var(--experience-card-radius)"
              color="white"
              lineHeight="normal"
              opacity={0}
              px="2"
              py="1.5"
              transition="opacity 0.1s ease-in-out"
            >
              {recommended}
            </Text>
          )}
        </Grid>
      </ControlLayer>

      <ControlLayer hasImage={hasImage} isCompact>
        <Flex align="center" h="100%" justify="center">
          {hasImage ? (
            <IconButton
              aria-label="Replace"
              icon={<Icon icon="Upload" size="22px" />}
              isDisabled={isRemovingImage}
              isLoading={isUploadingImage}
              onClick={onUploadClick}
            />
          ) : isUploadingImage ? (
            <IconButton
              aria-label="Uploading"
              as="span"
              icon={<Icon icon="Upload" size="22px" />}
              isLoading
            />
          ) : (
            <Icon icon="Upload" size="22px" />
          )}

          {hasImage && (
            <IconButton
              aria-label="Remove"
              borderRadius="full"
              icon={<Icon icon="X" size="14px" />}
              isDisabled={isUploadingImage}
              isLoading={isRemovingImage}
              pos="absolute"
              right="0"
              size="xs"
              top="0"
              transform="translate(30%, -30%)"
              onClick={onRemoveClick}
            />
          )}
        </Flex>
      </ControlLayer>
    </Flex>
  );
};
